import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from './api';
import type { AICallLogEntry, PaginatedResponse } from './types';

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (p: number) => void;
}> = ({ page, totalPages, total, onPageChange }) => (
  <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400">
    <span>共 {total.toLocaleString()} 筆</span>
    <div className="flex gap-2">
      <button
        onClick={() => onPageChange(page - 1)} disabled={page <= 1}
        className="px-3 py-1 border dark:border-gray-600 rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
      >上一頁</button>
      <span className="px-3 py-1">{page} / {totalPages}</span>
      <button
        onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
        className="px-3 py-1 border dark:border-gray-600 rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
      >下一頁</button>
    </div>
  </div>
);

import { calcCostTWD } from './costUtils';

const OwnerAutocomplete: React.FC<{
  ownerMap: Record<string, string>;
  value: string;
  onChange: (displayValue: string, accountId: string) => void;
  onClear: () => void;
}> = ({ ownerMap, value, onChange, onClear }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const suggestions = value.trim()
    ? Object.entries(ownerMap).filter(([, name]) =>
        name.toLowerCase().includes(value.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        placeholder="依擁有者名稱篩選"
        value={value}
        onChange={e => { onChange(e.target.value, ''); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded shadow-lg max-h-48 overflow-auto text-sm">
          {suggestions.map(([accountId, name]) => (
            <li
              key={accountId}
              onMouseDown={() => { onChange(name, accountId); setOpen(false); }}
              className="px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-800 dark:text-gray-200"
            >
              {name}
            </li>
          ))}
        </ul>
      )}
      {value && (
        <button
          type="button"
          onMouseDown={() => { onClear(); setOpen(false); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs"
        >✕</button>
      )}
    </div>
  );
};

const AiCallLogs: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse<AICallLogEntry> | null>(null);
  const [ownerMap, setOwnerMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [ownerNameInput, setOwnerNameInput] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');

  useEffect(() => {
    adminApi.getAccountOwnerMap().then(setOwnerMap).catch(() => {});
  }, []);

  const load = (p: number, aid: string) => {
    setLoading(true);
    adminApi.getAiCallLogs({ page: p, accountId: aid || undefined })
      .then(setData)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(page, selectedAccountId); }, [page, selectedAccountId]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    // selectedAccountId is already set via autocomplete selection
  };

  const handleClear = () => {
    setOwnerNameInput('');
    setSelectedAccountId('');
    setPage(1);
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-5 text-gray-800 dark:text-gray-100">AI 呼叫記錄</h2>

      <form onSubmit={handleFilter} className="flex gap-3 mb-5 items-center">
        <OwnerAutocomplete
          ownerMap={ownerMap}
          value={ownerNameInput}
          onChange={(name, accountId) => {
            setOwnerNameInput(name);
            setSelectedAccountId(accountId);
          }}
          onClear={() => { setOwnerNameInput(''); setSelectedAccountId(''); }}
        />
        <button type="submit" className="bg-blue-600 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-700">
          搜尋
        </button>
        {ownerNameInput && (
          <button type="button" onClick={handleClear} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            清除
          </button>
        )}
      </form>

      {loading ? (
        <div className="text-gray-500 dark:text-gray-400">載入中...</div>
      ) : data ? (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">擁有者</th>
                  <th className="px-4 py-3 text-left">步驟</th>
                  <th className="px-4 py-3 text-left">模型</th>
                  <th className="px-4 py-3 text-right">輸入 Token</th>
                  <th className="px-4 py-3 text-right">輸出 Token</th>
                  <th className="px-4 py-3 text-right">思考 Token</th>
                  <th className="px-4 py-3 text-right">費用 (TWD)</th>
                  <th className="px-4 py-3 text-left">時間</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.data.map(log => (
                  <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300 max-w-24 truncate" title={log.accountId}>
                      {ownerMap[log.accountId] || log.accountId}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{log.step}</td>
                    <td className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300">{log.modelName}</td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{log.inputTokens.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{log.outputTokens.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{log.thoughtTokens.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                      {calcCostTWD(log.modelName, log.inputTokens, log.outputTokens, log.thoughtTokens) ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString('zh-TW')}
                    </td>
                  </tr>
                ))}
                {data.data.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">無記錄</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} totalPages={data.totalPages} total={data.total} onPageChange={p => setPage(p)} />
        </>
      ) : null}
    </div>
  );
};

export default AiCallLogs;
